/* DriveCore theme behaviour. Vanilla custom elements, no dependencies. */
(function () {
  'use strict';

  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'es', {
      style: 'currency',
      currency: window.DcTheme?.currency || 'EUR',
    }).format(cents / 100);

  const announce = (message) => {
    const region = document.getElementById('DcLiveRegion');
    if (region) region.textContent = message;
  };

  // Placeholders arrive as [[name]] — see the note in layout/theme.liquid.
  const t = (key, replacements) => {
    let str = window.DcTheme?.strings?.[key] || '';
    if (replacements) {
      Object.keys(replacements).forEach((k) => {
        str = str.replace(`[[${k}]]`, replacements[k]);
      });
    }
    return str;
  };

  /* Focus trapping ---------------------------------------------------------- */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class Overlay extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
    }

    open(trigger) {
      this.opener = trigger || document.activeElement;
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => {
        const target = this.querySelector('[data-autofocus]') || this.querySelector(FOCUSABLE);
        target?.focus();
      });
    }

    close() {
      this.removeAttribute('open');
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onKeydown);
      this.opener?.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /* Mobile navigation ------------------------------------------------------- */

  class DcNavDrawer extends Overlay {
    connectedCallback() {
      this.querySelectorAll('[data-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      document
        .querySelectorAll(`[data-nav-toggle="${this.id}"]`)
        .forEach((btn) => btn.addEventListener('click', () => this.open(btn)));
    }
  }

  /* Cart -------------------------------------------------------------------- */

  const CartBus = {
    listeners: new Set(),
    subscribe(fn) {
      this.listeners.add(fn);
      return () => this.listeners.delete(fn);
    },
    publish(cart) {
      this.listeners.forEach((fn) => fn(cart));
    },
  };

  async function cartRequest(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'cart error');
    return data;
  }

  async function refreshCart() {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
    const cart = await response.json();
    CartBus.publish(cart);
    return cart;
  }

  class DcCartDrawer extends Overlay {
    connectedCallback() {
      this.body = this.querySelector('[data-cart-body]');
      this.querySelectorAll('[data-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });
      this.addEventListener('change', this.onQuantityChange.bind(this));
      this.addEventListener('click', this.onRemove.bind(this));
      CartBus.subscribe(() => this.render());
    }

    onRemove(event) {
      const button = event.target.closest('[data-cart-remove]');
      if (!button) return;
      event.preventDefault();
      this.updateLine(button.dataset.cartRemove, 0);
    }

    onQuantityChange(event) {
      const input = event.target.closest('[data-cart-quantity]');
      if (!input) return;
      this.updateLine(input.dataset.cartQuantity, parseInt(input.value, 10));
    }

    async updateLine(key, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        await cartRequest(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          id: key,
          quantity,
        });
        await refreshCart();
      } catch (error) {
        announce(t('cartUpdateError'));
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async render() {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`
      );
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-body]');
      if (fresh && this.body) this.body.innerHTML = fresh.innerHTML;
    }
  }

  class DcCartCount extends HTMLElement {
    connectedCallback() {
      CartBus.subscribe((cart) => {
        this.textContent = cart.item_count;
        this.hidden = cart.item_count === 0;
        const label = this.closest('[data-cart-link]');
        if (label) {
          label.setAttribute(
            'aria-label',
            t(cart.item_count === 1 ? 'cartCountOne' : 'cartCountOther', {
              count: cart.item_count,
            })
          );
        }
      });
    }
  }

  class DcAddToCart extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.form?.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.disabled) return;

      const original = this.button.textContent;
      this.button.disabled = true;
      this.button.textContent = t('adding');

      try {
        const formData = new FormData(this.form);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'add error');

        this.button.textContent = t('added');
        announce(t('itemAdded', { title: data.product_title || '' }));
        await refreshCart();

        if (window.DcTheme?.cartType === 'drawer') {
          document.querySelector('dc-cart-drawer')?.open(this.button);
        }
      } catch (error) {
        this.button.textContent = original;
        announce(error.message);
      } finally {
        setTimeout(() => {
          this.button.disabled = false;
          this.button.textContent = original;
        }, 1400);
      }
    }
  }

  /* Quantity ---------------------------------------------------------------- */

  class DcQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', () => {
          const step = button.dataset.step === 'up' ? 1 : -1;
          const min = parseInt(this.input.min, 10) || 1;
          const next = Math.max(min, (parseInt(this.input.value, 10) || min) + step);
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }

  /* Product gallery --------------------------------------------------------- */

  class DcGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-gallery-viewport]');
      this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.counter = this.querySelector('[data-gallery-counter]');

      this.thumbs.forEach((thumb, index) =>
        thumb.addEventListener('click', () => this.goTo(index))
      );

      if (this.viewport && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
            });
          },
          { root: this.viewport, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    goTo(index) {
      this.slides[index]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    setActive(index) {
      if (index < 0) return;
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    }
  }

  /* Variants ---------------------------------------------------------------- */

  class DcVariants extends HTMLElement {
    connectedCallback() {
      this.productHandle = this.dataset.handle;
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', this.onChange.bind(this));
    }

    get selectedOptions() {
      return [...this.querySelectorAll('input[type="radio"]:checked, select')].map(
        (input) => input.value
      );
    }

    async onChange() {
      const url = `${window.Shopify?.routes?.root || '/'}products/${this.productHandle}?${new URLSearchParams(
        { section_id: this.sectionId, option_values: this.selectedOptions.join(',') }
      )}`;

      try {
        const response = await fetch(url);
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        ['[data-price-block]', '[data-atc-block]', '[data-sku-block]', '[data-availability]'].forEach(
          (selector) => {
            const fresh = parsed.querySelector(selector);
            const current = document.querySelector(selector);
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          }
        );

        const variantInput = parsed.querySelector('[name="id"]');
        if (variantInput) {
          document
            .querySelectorAll('form[data-product-form] [name="id"]')
            .forEach((input) => (input.value = variantInput.value));
          const location = new URL(window.location);
          location.searchParams.set('variant', variantInput.value);
          window.history.replaceState({}, '', location);
        }
      } catch (error) {
        /* Leave the current selection rendered rather than blanking the form. */
      }
    }
  }

  /* Sticky add to cart ------------------------------------------------------ */

  class DcStickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.watch);
      if (!anchor || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', passed);
        },
        { threshold: 0 }
      );
      this.observer.observe(anchor);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  /* Predictive search -------------------------------------------------------- */

  class DcSearch extends Overlay {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      this.querySelectorAll('[data-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      document
        .querySelectorAll(`[data-search-toggle="${this.id}"]`)
        .forEach((btn) => btn.addEventListener('click', () => this.open(btn)));

      this.input?.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('click', (event) => {
        if (event.target === this) this.close();
      });
    }

    onInput() {
      clearTimeout(this.timer);
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.timer = setTimeout(() => this.search(term), 250);
    }

    async search(term) {
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': 6,
        section_id: 'predictive-search',
      });
      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}search/suggest?${params}`
        );
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('[data-search-results]');
        if (fresh) this.results.innerHTML = fresh.innerHTML;
      } catch (error) {
        this.results.innerHTML = '';
      }
    }
  }

  /* Reveal on scroll --------------------------------------------------------- */

  function initReveal() {
    const items = document.querySelectorAll('.dc-reveal');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );
    items.forEach((item) => observer.observe(item));
  }

  /* Header scroll state ------------------------------------------------------ */

  function initHeaderState() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* Registration ------------------------------------------------------------- */

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define('dc-nav-drawer', DcNavDrawer);
  define('dc-cart-drawer', DcCartDrawer);
  define('dc-cart-count', DcCartCount);
  define('dc-add-to-cart', DcAddToCart);
  define('dc-quantity', DcQuantity);
  define('dc-gallery', DcGallery);
  define('dc-variants', DcVariants);
  define('dc-sticky-atc', DcStickyAtc);
  define('dc-search', DcSearch);

  /* Cart link intercept ------------------------------------------------------ */

  function initCartTriggers() {
    document.querySelectorAll('[data-cart-open]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const drawer = document.querySelector('dc-cart-drawer');
        if (!drawer) return;
        event.preventDefault();
        drawer.open(link);
      });
    });
  }

  /* Product recommendations -------------------------------------------------- */

  function initRecommendations() {
    const holder = document.querySelector('[data-recommendations]');
    if (!holder || !holder.dataset.url) return;

    fetch(holder.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const fresh = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          holder.innerHTML = fresh.innerHTML;
          initReveal();
        }
      })
      .catch(() => {});
  }

  const boot = () => {
    initReveal();
    initHeaderState();
    initCartTriggers();
    initRecommendations();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', initReveal);

  window.DcCart = { refresh: refreshCart, subscribe: CartBus.subscribe.bind(CartBus), money };
})();
